import { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ExternalLink, Ban, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const GingrLink = ({ c }) => c.gingr_profile_url ? (
  <a href={c.gingr_profile_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Open customer profile in Gingr"
    className="inline-flex text-gray-400 hover:text-[#1F8A7C] transition-colors" data-testid={`pkg-gingr-link-${c.owner_id}`}>
    <ExternalLink className="h-3.5 w-3.5" />
  </a>
) : null;

export const NoInterestBadge = ({ c }) => c.no_package_interest ? (
  <span title={c.no_package_interest_note || "Tagged in Gingr"} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold border bg-gray-100 text-gray-700 border-gray-300 max-w-[280px] shrink-0" data-testid={`pkg-no-interest-${c.owner_id}`}>
    <Ban className="h-3 w-3 shrink-0" /><span className="truncate">No package interest{c.no_package_interest_note ? ` · ${c.no_package_interest_note}` : ""}</span>
  </span>
) : null;

// Gingr owner icons (Card on File, Referral…) as small neutral labels; color only as a dot.
export const OwnerIcons = ({ c }) => (
  <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap overflow-hidden [mask-image:linear-gradient(to_right,#000_88%,transparent)]" title={(c.owner_icons || []).map((i) => i.comment ? `${i.title}: ${i.comment}` : i.title).join("\n")} data-testid={`pkg-owner-icons-${c.owner_id}`}>
    <GingrLink c={c} />
    <NoInterestBadge c={c} />
    {(c.owner_icons || []).filter((i) => !c.no_package_interest || !/no package|not interested/i.test(i.title)).map((i) => (
      <span key={i.title} title={i.comment || i.title} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border border-gray-200 bg-white text-gray-600 whitespace-nowrap shrink-0">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: i.color }} />{i.title}
      </span>
    ))}
  </div>
);

export const STATUS_META = {
  out: { label: "Out of credits", badge: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500", action: "Sell renewal now" },
  low: { label: "Low", badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", action: "Mention renewal" },
  out_check: { label: "Out (from last visit)", badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", action: "Check Gingr before selling" },
  no_package: { label: "No package", badge: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500", action: "Offer a package" },
};

// Credits rebuilt from visits (shared Gingr key) can miss a pack that hasn't been used yet.
export const statusKey = (c) => (c.status === "out" && c.reconstructed_from_pos ? "out_check" : c.status);

export const StatusPill = ({ status, tid }) => {
  const m = STATUS_META[status] || STATUS_META.low;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border whitespace-nowrap ${m.badge}`} data-testid={tid}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
};

const TH = "text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 px-3 py-3";
const STATUS_ORDER = { out: 0, out_check: 1, low: 2, no_package: 3 };
const actionOf = (c) => (c.no_package_interest ? "Don't pitch · no interest" : (STATUS_META[statusKey(c)] || STATUS_META.low).action);
export const ownerLabel = (c) => [c.owner_first, c.owner_name].filter(Boolean).join(" ") || "Unknown";
const pkgOf = (c) => (c.status === "no_package" ? "" : (c.daycare_packages && c.daycare_packages[0]?.name) || "");

const SORTERS = {
  owner: (c) => `${(c.owner_name || "").toLowerCase()} ${(c.owner_first || "").toLowerCase()}`,
  dogs: (c) => (c.dogs || []).filter(Boolean).join(", ").toLowerCase(),
  package: (c) => pkgOf(c).toLowerCase(),
  credits: (c) => (c.status === "no_package" ? 1000 + (c.recent_daycare_visits || 0) : c.daycare_remaining ?? 0),
  status: (c) => STATUS_ORDER[statusKey(c)] ?? 9,
  action: (c) => actionOf(c).toLowerCase(),
};

const SortTH = ({ id, label, sort, onSort, className = "" }) => {
  const active = sort.key === id;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`${TH} ${className}`} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(id)} data-testid={`pkg-sort-${id}`}
        className={`inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wide transition-colors hover:text-[#1F8A7C] ${active ? "text-[#1F8A7C]" : ""}`}>
        {label}<Icon className={`h-3 w-3 ${active ? "" : "opacity-50"}`} />
      </button>
    </th>
  );
};

// At-a-glance table of today's renewal candidates; checkbox shares selection with the drafts list.
export const RenewalsTable = ({ candidates, selected, toggle, selectable, onJump }) => {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const rows = useMemo(() => {
    if (!sort.key) return candidates;
    const f = SORTERS[sort.key];
    const m = sort.dir === "asc" ? 1 : -1;
    return [...candidates].sort((a, b) => {
      const x = f(a), y = f(b);
      return (typeof x === "number" ? x - y : String(x).localeCompare(String(y))) * m;
    });
  }, [candidates, sort]);
  const th = { sort, onSort };
  return (
  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-x-auto" data-testid="pkg-candidates-table">
    <table className="w-full text-sm">
      <thead className="border-b border-gray-100">
        <tr>
          <th className="w-10 px-3" />
          <SortTH id="owner" label="Owner" {...th} />
          <SortTH id="dogs" label="Dogs" {...th} />
          <SortTH id="package" label="Daycare package" className="hidden md:table-cell" {...th} />
          <SortTH id="credits" label="Credits left" {...th} />
          <SortTH id="status" label="Status" className="hidden sm:table-cell" {...th} />
          <SortTH id="action" label="Suggested action" className="hidden lg:table-cell" {...th} />
        </tr>
      </thead>
      <tbody>
        {rows.map((c, idx) => {
          const id = String(c.owner_id);
          const none = c.status === "no_package";
          const pkg = pkgOf(c) || "—";
          return (
            <tr key={id} className={`border-b border-[#1F8A7C]/10 last:border-0 cursor-pointer transition-colors duration-150 hover:bg-[#13212A]/[0.04] ${idx % 2 ? "bg-[#F5F9F9]" : "bg-white"}`}
              onClick={() => onJump(id)} data-testid={`pkg-table-row-${id}`}>
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={selected.has(id)} disabled={!selectable(id)} onCheckedChange={() => toggle(id)} data-testid={`pkg-table-select-${id}`} />
              </td>
              <td className="px-3 py-3 max-w-[330px]"><div className="font-bold text-gray-900 whitespace-nowrap" data-testid={`pkg-table-owner-${id}`}>{ownerLabel(c)}</div><OwnerIcons c={c} /></td>
              <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{(c.dogs || []).filter(Boolean).join(", ") || "—"}</td>
              <td className={`px-3 py-3 hidden md:table-cell min-w-[170px] ${none ? "text-gray-400" : "text-gray-700"}`}>{none ? "none — pay per visit" : pkg}</td>
              <td className="px-3 py-3 whitespace-nowrap">
                {none ? <span className="text-gray-500">{c.recent_daycare_visits ?? "?"}<span className="hidden sm:inline"> visits</span> / 90d</span>
                  : <span className="font-bold text-gray-900">{c.daycare_remaining}</span>}
              </td>
              <td className="px-3 py-3 hidden sm:table-cell"><StatusPill status={statusKey(c)} tid={`pkg-table-status-${id}`} /></td>
              <td className="px-3 py-3 hidden lg:table-cell font-semibold text-gray-800 whitespace-nowrap">{actionOf(c)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
  );
};
